import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { Plus } from "lucide-react";
import React, { useState } from "react";
import { useRef } from "react";
import EstimateCreateDialog from "../EstimateCreateDialog";
import { showToast } from "@/utils/toastUtils";
import { useSelectedProjectInfo } from "../../hooks/useTabStore";
import {
  useCreateProjectGroupsItemSheetFromFile,
  useCreateProjectGroupsItemSheetFromOriginal,
} from "@/libs/react-query/hooks/useItemsheetsQuery";
import { useParams } from "react-router-dom";
import { useIsEditing, useIsExcelDownloading } from "../../hooks/useTableStore";

export default function EstimateTabsCreate() {
  const { group_id = "" } = useParams();

  const [popoverOpen, setPopoverOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const selectedProjectInfo = useSelectedProjectInfo();
  const isEditing = useIsEditing();
  const isExcelDownloading = useIsExcelDownloading();

  const { mutate: createEstimate } =
    useCreateProjectGroupsItemSheetFromOriginal(group_id);
  const { mutate: uploadExcel } =
    useCreateProjectGroupsItemSheetFromFile(group_id);

  const onSubmit = (data: { estimateName: string }) => {
    if (!selectedProjectInfo) return;
    const { projectId, projectType, originalId } = selectedProjectInfo;

    createEstimate(
      {
        original_id: originalId,
        project_id: projectId,
        project_type: projectType,
        title: data.estimateName,
      },
      {
        onSuccess: () => {
          showToast({ message: "산출내역서가 생성되었습니다." });
        },
        onError: (error: any) => {
          showToast({
            message: "산출내역서 생성에 실패했습니다.",
            type: "error",
            description: error?.response?.data?.display_message || "",
          });
        },
      }
    );

    setDialogOpen(false);
    setPopoverOpen(false);
  };

  const handleExcelUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!selectedProjectInfo) return;
    const { projectId, projectType, originalId } = selectedProjectInfo;
    const file = e.target.files?.[0];
    if (!file) return;

    uploadExcel(
      {
        original_id: originalId,
        project_id: projectId,
        project_type: projectType,
        uploaded_file: file,
      },
      {
        onSuccess: () => showToast({ message: "엑셀 업로드 완료" }),
        onError: (error: any) => {
          showToast({
            message: "엑셀 업로드 실패",
            type: "error",
            description: error?.response?.data?.display_message || "",
          });
        },
      }
    );

    e.target.value = "";
    setPopoverOpen(false);
  };

  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger disabled={isEditing || isExcelDownloading}>
        <Plus size={20} />
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0">
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger>
            <div className="px-5 py-2 inline-flex hover:bg-gray-E">
              <span className="text-[8px] text-gray-A font-semibold">
                새 산출내역서 추가
              </span>
            </div>
          </DialogTrigger>
          <EstimateCreateDialog
            onSubmit={onSubmit}
            title="산출내역서 추가"
            buttonText="추가하기"
            noLabel
          />
        </Dialog>

        <div
          className="px-5 py-2 flex items-center gap-0.5 cursor-pointer hover:bg-gray-E"
          onClick={() => fileInputRef.current?.click()}
        >
          <span className="text-[8px] text-gray-A font-semibold">
            Excel로 추가
          </span>
          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx, .xls, .csv"
            className="hidden"
            onChange={handleExcelUpload}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}
